import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { esFechaValida, esHoraValida } from '../utils/dateTimeValidation';
import { esNombreValido } from '../utils/nameValidation';
import { formatCityCountry } from '../utils/citySearch';
import { useScrollReset } from '../utils/useScrollReset';
import { useCityAutocomplete } from '../utils/useCityAutocomplete';

const CalculationFormPopup = ({ isOpen, onClose, onSubmit, anchorEl }) => {
  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const popupRef = useRef(null);
  const [fecha, setFecha] = useState('');
  const [fechaVisible, setFechaVisible] = useState('');
  const [hora, setHora] = useState('');
  const [horaVisible, setHoraVisible] = useState('');

  // Un solo campo de ciudad: país y coordenadas salen de la fila elegida.
  const cityAutocomplete = useCityAutocomplete();
  const pais = cityAutocomplete.selectedCountry || '';
  const ciudad = cityAutocomplete.selectedCity
    ? {
        label: cityAutocomplete.selectedCity,
        lat: cityAutocomplete.selectedLat,
        lng: cityAutocomplete.selectedLng,
      }
    : null;
  const { t, i18n } = useTranslation();
  const [errorFecha, setErrorFecha] = useState('');
  const [errorHora, setErrorHora] = useState('');
  const [errorNombre, setErrorNombre] = useState('');
  const [errorApellido, setErrorApellido] = useState('');
  const [sinHora, setSinHora] = useState(false);

  useEffect(() => {
    if (isOpen && anchorEl && popupRef.current) {
      const anchorRect = anchorEl.getBoundingClientRect();
      const popupElement = popupRef.current;
      popupElement.style.top = `${anchorRect.bottom + 10}px`;
      popupElement.style.left = `${anchorRect.left}px`;
      popupElement.style.minWidth = `${anchorRect.width}px`;
    }
  }, [isOpen, anchorEl]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        anchorEl &&
        !anchorEl.contains(event.target)
      ) {
        onClose();
        setNombre('');
        setApellido('');
        setFecha('');
        setFechaVisible('');
        setHora('');
        setHoraVisible('');
        setSinHora(false);
        cityAutocomplete.hydrate({ city: '' });
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, onClose, anchorEl]);
  const popupVariants = {
    open: { scale: 1, opacity: 1, transition: { type: 'spring', stiffness: 200, damping: 25 } },
    closed: { scale: 0, opacity: 0, transition: { duration: 0.1 } },
    exit: { scale: 0, opacity: 0, transition: { duration: 0.1 } },
  };
  const ciudadListRef = useScrollReset(cityAutocomplete.searchText, cityAutocomplete.results);

  const currentLanguage = i18n.language;

  const [formularioInvalido, setFormularioInvalido] = useState(true);
  const validateFecha = (selectedDate) => {
    if (!selectedDate) {
      setErrorFecha('');
      return false;
    }
    const [year, month, day] = selectedDate.split('-');
    if (!esFechaValida(day, month, year)) {
      setErrorFecha(t('form.fecha_error_rango'));
      return false;
    }
    setErrorFecha('');
    return true;
  };
  const handleFechaChange = (e) => {
    let value = e.target.value;
    const formattedValue = value.replace(/\D/g, '');
    if (formattedValue.length > 2 && formattedValue.length <= 4) {
      value = `${formattedValue.slice(0, 2)}/${formattedValue.slice(2)}`;
    } else if (formattedValue.length > 4) {
      value = `${formattedValue.slice(0, 2)}/${formattedValue.slice(2, 4)}/${formattedValue.slice(4, 8)}`;
    } else {
      value = formattedValue;
    }
    setFechaVisible(value);
    if (formattedValue.length === 8) {
      const day = formattedValue.slice(0, 2);
      const month = formattedValue.slice(2, 4);
      const year = formattedValue.slice(4, 8);
      setFecha(`${year}-${month}-${day}`);
    } else {
      setFecha('');
    }
  };
  const handleHoraChange = (e) => {
    let value = e.target.value;
    const formattedValue = value.replace(/[^0-9:]/g, '');
    if (formattedValue.length > 2 && formattedValue.indexOf(':') === -1) {
      value = `${formattedValue.slice(0, 2)}:${formattedValue.slice(2, 4)}`;
    } else {
      value = formattedValue.slice(0, 5);
    }
    setHoraVisible(value);
    setHora(value);
  };

  const handleSinHoraChange = (e) => {
    const isChecked = e.target.checked;
    setSinHora(isChecked);
    if (isChecked) {
      setHora('');
      setHoraVisible('');
    }
  };

  React.useEffect(() => {
    const fechaOk = validateFecha(fecha);
    const horaOk = sinHora || esHoraValida(hora);
    setErrorHora(!sinHora && hora.length === 5 && !horaOk ? t('form.hora_error') : '');
    const nombreOk = esNombreValido(nombre);
    const apellidoOk = !apellido || esNombreValido(apellido);
    setErrorNombre(nombre && !nombreOk ? t('form.nombre_error') : '');
    setErrorApellido(apellido && !apellidoOk ? t('form.apellido_error') : '');
    const camposCompletos = nombre && fecha && (sinHora || hora) && pais && ciudad?.label;
    setFormularioInvalido(!fechaOk || !horaOk || !nombreOk || !apellidoOk || !camposCompletos);
  }, [nombre, apellido, fecha, hora, pais, ciudad, sinHora]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const fechaOk = validateFecha(fecha);
    const horaOk = sinHora || esHoraValida(hora);
    const nombreOk = esNombreValido(nombre);
    const apellidoOk = !apellido || esNombreValido(apellido);
    if (!fechaOk || !horaOk || !nombreOk || !apellidoOk) {
      setErrorHora(!horaOk ? t('form.hora_error') : '');
      setErrorNombre(!nombreOk ? t('form.nombre_error') : '');
      setErrorApellido(!apellidoOk ? t('form.apellido_error') : '');
      return;
    }
    const horaAEnviar = sinHora ? null : hora;
    onSubmit({
      nombre,
      apellido,
      fecha,
      hora: horaAEnviar,
      pais,
      ciudad: ciudad?.label || '',
      ...(ciudad?.lat != null && ciudad?.lng != null ? { lat: ciudad.lat, lng: ciudad.lng } : {}),
    });
    setNombre('');
    setApellido('');
    setFecha('');
    setFechaVisible('');
    setHora('');
    setHoraVisible('');
    setSinHora(false);
    cityAutocomplete.hydrate({ city: '' });
    setErrorFecha('');
    setErrorHora('');
    setErrorNombre('');
    setErrorApellido('');
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <motion.div
      ref={popupRef}
      className="calculation-form-popup"
      initial="closed"
      animate={isOpen ? 'open' : 'exit'}
      variants={popupVariants}
    >
      <form
        style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}
        onSubmit={handleSubmit}
      >
        <div>
          <input
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            className="form-input"
            placeholder={t('form.nombre')}
            style={{ textAlign: 'start' }}
          />
          {errorNombre && <p style={{ color: 'red', fontSize: '.75rem' }}>{errorNombre}</p>}
        </div>
        <div>
          <input
            type="text"
            value={apellido}
            onChange={(e) => setApellido(e.target.value)}
            className="form-input"
            placeholder={t('form.apellido')}
            style={{ textAlign: 'start' }}
          />
          {errorApellido && <p style={{ color: 'red', fontSize: '.75rem' }}>{errorApellido}</p>}
        </div>
        <div>
          <label className="calculation-form-label" style={{ fontSize: '.85rem' }}>
            {t('form.fecha_nacimiento')}
          </label>
          <input
            type="text"
            value={fechaVisible}
            onChange={handleFechaChange}
            className="form-input date-input"
            placeholder="DD/MM/YYYY"
            maxLength="10"
            style={{ color: 'grey', textAlign: 'center' }}
          />
          {errorFecha && <p style={{ color: 'red', fontSize: '.75rem' }}>{errorFecha}</p>}
        </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginTop: '0.5rem',
          justifyContent: 'flex-start',
           flexDirection:'row'
        }}
      >
        <label className="custom-checkbox-container" style={{display:'flex', flexDirection:'row'}}>
          <input type="checkbox" checked={sinHora} onChange={handleSinHoraChange} />
          <span className="custom-checkmark round"></span>
          <span className="checkbox-label">Sin hora</span>
        </label>
      </div>
        <div>
          <label className="calculation-form-label" style={{ fontSize: '.85rem' }}>
            {t('form.hora_nacimiento')}
          </label>
          <input
            type="text"
            value={horaVisible}
            onChange={handleHoraChange}
            className="form-input date-input"
            placeholder="HH:MM"
            maxLength="5"
            disabled={sinHora}
            style={{ color: 'grey', textAlign: 'center', opacity: sinHora ? 0.2 : 1 }}
          />
          {errorHora && <p style={{ color: 'red', fontSize: '.75rem' }}>{errorHora}</p>}
        </div>
        {/* Ciudad de nacimiento: busca en todos los países a la vez */}
        <div className="popup-dropdown">
          <div
            className={`popup-dropdown-header ${cityAutocomplete.isOpen ? 'popup-dropdown-header-open' : ''}`}
            onClick={() => cityAutocomplete.setIsOpen(true)}
          >
            <input
              autoComplete="off"
              type="text"
              className="dropdown-search-input"
              placeholder={t('form.select_city_search')}
              value={cityAutocomplete.searchText}
              onChange={(e) => cityAutocomplete.onChangeText(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onFocus={cityAutocomplete.onFocus}
              onBlur={cityAutocomplete.onBlur}
            />
            <svg viewBox="0 0 24 24" fill="currentColor" className="dropdown-icon">
              <path d="M7 10l5 5 5-5z" />
            </svg>
          </div>
          {cityAutocomplete.isOpen && (
            <ul ref={ciudadListRef} className={`popup-dropdown-list ${cityAutocomplete.isOpen ? 'open' : ''}`}>
              {cityAutocomplete.results.length > 0 ? (
                cityAutocomplete.results.map((item) => (
                  <li
                    key={`${item.country}-${item.value}-${item.lat}-${item.lng}`}
                    onClick={() => cityAutocomplete.onSelect(item)}
                  >
                    {formatCityCountry(item, currentLanguage)}
                  </li>
                ))
              ) : cityAutocomplete.isLoading ? (
                <li className="no-results">{t('loading')}</li>
              ) : (
                <li className="no-results">{t('no_results')}</li>
              )}
            </ul>
          )}
        </div>
        <div className="popup-actions">
          <button
            type="submit"
            disabled={formularioInvalido}
            style={{
              padding: '0.4375rem 1.25rem',
              backgroundColor: 'var(--light-grey)',
              border: 'none',
              borderRadius: '1.25rem',
              fontFamily: 'Effra',
              fontSize: '0.8rem',
              cursor: formularioInvalido ? 'not-allowed' : 'pointer',
              opacity: formularioInvalido ? 0.5 : 1,
              marginLeft: 'auto',
              color: '#808080',
              transition: 'background-color 0.3s ease',
            }}
            onMouseOver={(e) => {
              if (formularioInvalido) return;
              e.target.style.backgroundColor = 'var(--grey-hover)';
              e.target.style.color = '#1a1a1a';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'var(--light-grey)';
              e.target.style.color = '#808080';
            }}
          >
            {t('calculate')}
          </button>
        </div>
      </form>
    </motion.div>
  );
};
export default CalculationFormPopup;
